import * as React from 'react';
import { SortableElement, SortableHandle } from 'react-sortable-hoc';
import ReactTooltip from 'react-tooltip';

import classnames from 'classnames';
import { flatQuestionTypes } from '@nexxt/common/questionTypes';
import SortableCard from './SortableCard';
import {
    QTYPES,
    HealthProblemType,
    HealthProblemContext,
    QuestionID,
    Project,
    QuestionSummary
} from '@nexxt/common/types';
import HealthIcon from '../../common/designElements/HealthIcon';
import Icons from '../../common/designElements/Icons';
import QuestionAddButton from './common/QuestionAddButton';
import { PROJECT_EDIT_MODES } from '../../../interface/project.interface';
import { LOCK_PROJECT } from '../../../constants/project';

const QuestionTypesObject = {};
flatQuestionTypes.forEach((q) => {
    QuestionTypesObject[q.value] = q;
});

export interface CardProps {
    questionSummary: QuestionSummary;
    position: number;
    selectedQuestionId: QuestionID;
    languages: any[];
    editEnabled: boolean;
    projectHealth: any;
    health?: any;
    activeMenuID: number;
    toggleActiveMenuID: (id: QuestionID) => void;
    moveCard: (
        dragIndex: number,
        hoverIndex: number,
        groupQuestionId: number,
        collection: number
    ) => void;
    onSelectQuestion: (
        questionSummary: QuestionSummary,
        forceParent?: boolean
    ) => void;
    actions: {
        edit: (question: any) => void;
        preview: (question: any) => void;
        delete: (question: any) => void;
        logic: (question: any) => void;
        duplicate: (question: any) => void;
    };
    onAdd: (addPosition?: number, parent?: QuestionID) => void;
    dragStop: () => void;
    isBottom?: boolean;
    currentProject: Project;
    editMode: PROJECT_EDIT_MODES;
    selected?: boolean;
    initialSelectedQuestionID: number;
    showError: boolean;
}

interface CardStates {
    showCardList: boolean;
    showAddQuestionButton: boolean;
    disabled: boolean;
}
class SortableGroupCard extends React.Component<CardProps, CardStates> {
    childrenCardList: HTMLDivElement;
    state = {
        showCardList: true,
        showAddQuestionButton: false,
        disabled: false
    };

    componentDidMount() {
        this.getTemplateSettings();
    }

    componentDidUpdate(prevProps) {
        if (this.props.editEnabled !== prevProps.editEnabled) {
            this.getTemplateSettings();
        }
    }

    getTemplateSettings = () => {
        const { editMode, currentProject, questionSummary } = this.props;
        try {
            const templateSettings = LOCK_PROJECT[
                editMode as PROJECT_EDIT_MODES
            ][currentProject?.templateCode]?.questions?.find(
                (q) => q.title === questionSummary.label
            );

            this.setState({ disabled: !templateSettings });
        } catch (err) {
            this.setState({ disabled: !this.props.editEnabled });
        }
    };

    handleMouseOverAddQuestionButton = (event, show) => {
        event.stopPropagation(); // don't let the event recurse into children

        this.setState({
            showAddQuestionButton: show
        });
    };
    addNewQuestion = (e, position) => {
        e.stopPropagation();
        this.props.onAdd(position, this.props.questionSummary.parent);
    };
    onClickMenuItem = (e, actionType) => {
        e.stopPropagation();
        this.props.toggleActiveMenuID(-1);
        this.props.actions[actionType](this.props.questionSummary);
    };
    render() {
        const { props } = this;
        const { showAddQuestionButton, disabled } = this.state;
        // if there is wrong children, filter it
        const filteredChildren = (
            props.questionSummary.childrenSummaries || []
        ).filter((x) => x);
        const healthType =
            props.health && props.health.type === HealthProblemType.ERROR
                ? 'error'
                : 'warning';
        const conditionProb = [],
            questionProb = [];
        props.health &&
            props.health.list.forEach((problem) =>
                problem.context === HealthProblemContext.CONDITION
                    ? conditionProb.push(problem)
                    : questionProb.push(problem)
            );
        return (
            <div className="grouped">
                <div
                    className={classnames(`question-card`, {
                        active:
                            props.selectedQuestionId ===
                            props.questionSummary.id,
                        disabled
                    })}
                    onClick={() => {
                        props.onSelectQuestion(props.questionSummary);
                    }}
                >
                    <QuestionAddButton
                        questionID={props.questionSummary.id}
                        onMouseEvent={this.handleMouseOverAddQuestionButton}
                        addQuestion={(e) =>
                            this.addNewQuestion(
                                e,
                                props.questionSummary.position
                            )
                        }
                        show={showAddQuestionButton}
                    />
                    {props.editEnabled && <DragHandle />}
                    <div className="q-card-pre-header">
                        {!!props.questionSummary.hasCondition && (
                            <span className="pre-header-icon logic">LOGIC</span>
                        )}
                        {!!props.questionSummary.hasCustomLogic && (
                            <span className="pre-header-icon logic">
                                CUSTOM
                            </span>
                        )}
                        {!!filteredChildren.length && (
                            <span
                                className="pre-header-icon arrow"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    this.setState({
                                        showCardList: !this.state.showCardList
                                    });
                                }}
                            >
                                <Icons type="arrow-down" width={20} />
                            </span>
                        )}
                        {props.editEnabled && (
                            <span
                                className="pre-header-icon menu"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    props.toggleActiveMenuID(
                                        props.questionSummary.id
                                    );
                                }}
                            >
                                <Icons type="eclipsis-h" />
                            </span>
                        )}
                    </div>

                    <div
                        className={`q-card-header q-card-header--grouped-label ${
                            !!props.questionSummary.hasCondition
                                ? 'condition-label'
                                : 'no-condition'
                        }`}
                    >
                        {!(
                            props.questionSummary.type === QTYPES.POPUP_CARD
                        ) && (
                            <span className="question-image">
                                <img
                                    src={
                                        QuestionTypesObject[
                                            props.questionSummary.type
                                        ].image
                                    }
                                />
                            </span>
                        )}
                        <span className="question-label">
                            {props.questionSummary.label ||
                                props.questionSummary.title}
                        </span>
                        <HealthIcon
                            questionID={props.questionSummary.id}
                            active={!!this.props.health}
                            type={props.health?.type}
                            className={`health ${props.health?.type?.toLowerCase()}`}
                            message={
                                this.props.health &&
                                this.props.health.list[0].message
                            }
                        />
                    </div>
                    <div className="q-card-body"></div>
                    {props.isBottom && (
                        <QuestionAddButton
                            questionID={props.questionSummary.id}
                            onMouseEvent={this.handleMouseOverAddQuestionButton}
                            addQuestion={(e) =>
                                this.addNewQuestion(
                                    e,
                                    props.questionSummary.position + 1
                                )
                            }
                            show={showAddQuestionButton}
                            customInlineStyle={{
                                bottom:
                                    this.state.showCardList &&
                                    `calc(-${this.childrenCardList?.offsetHeight}px - 20px)`
                            }}
                            bottom={true}
                        />
                    )}
                </div>
                {this.props.activeMenuID === props.questionSummary.id && (
                    <div
                        className="q-card-menu"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {!props.questionSummary.parent && (
                            <p
                                className="q-card-menu--item"
                                onClick={(e) =>
                                    this.onClickMenuItem(e, 'duplicate')
                                }
                            >
                                Duplicate
                            </p>
                        )}
                        <p
                            className="q-card-menu--item"
                            onClick={(e) => this.onClickMenuItem(e, 'delete')}
                        >
                            Delete
                        </p>
                    </div>
                )}

                {this.state.showCardList && (
                    <div
                        ref={(e) => (this.childrenCardList = e)}
                        className={classnames(`inner-level`, {
                            none: !filteredChildren.length
                        })}
                    >
                        {filteredChildren.map((childrenSummary, index) => {
                            const length = filteredChildren.length;
                            if (
                                childrenSummary.type === QTYPES.GROUP ||
                                childrenSummary.type === QTYPES.POPUP
                            ) {
                                return (
                                    <DefaultSortableGroupCard
                                        key={childrenSummary.id}
                                        collection={props.questionSummary.id}
                                        index={index}
                                        questionSummary={childrenSummary}
                                        position={childrenSummary.position}
                                        moveCard={props.moveCard}
                                        dragStop={props.dragStop}
                                        onSelectQuestion={
                                            props.onSelectQuestion
                                        }
                                        actions={props.actions}
                                        languages={props.languages}
                                        selectedQuestionId={
                                            props.selectedQuestionId
                                        }
                                        selected={false} // not in use
                                        editEnabled={this.props.editEnabled}
                                        projectHealth={this.props.projectHealth}
                                        health={
                                            props.projectHealth &&
                                            props.projectHealth[
                                                childrenSummary.id
                                            ]
                                        }
                                        activeMenuID={props.activeMenuID}
                                        toggleActiveMenuID={
                                            props.toggleActiveMenuID
                                        }
                                        onAdd={props.onAdd}
                                        isBottom={index === length - 1}
                                        currentProject={props.currentProject}
                                        editMode={props.editMode}
                                        initialSelectedQuestionID={
                                            props.initialSelectedQuestionID
                                        }
                                        showError={props.showError}
                                    />
                                );
                            }
                            return (
                                <SortableCard
                                    key={childrenSummary.id}
                                    collection={props.questionSummary.id}
                                    index={index}
                                    activeMenuID={props.activeMenuID}
                                    toggleActiveMenuID={
                                        props.toggleActiveMenuID
                                    }
                                    questionSummary={childrenSummary}
                                    position={childrenSummary.position}
                                    moveCard={props.moveCard}
                                    dragStop={props.dragStop}
                                    onSelectQuestion={props.onSelectQuestion}
                                    actions={props.actions}
                                    languages={props.languages}
                                    selected={
                                        props.selectedQuestionId ===
                                        childrenSummary.id
                                    }
                                    disabled={
                                        props.questionSummary.type ===
                                            QTYPES.POPUP && index === 0
                                    }
                                    editEnabled={props.editEnabled}
                                    parentQuestion={this.props.questionSummary}
                                    health={
                                        props.projectHealth &&
                                        props.projectHealth[childrenSummary.id]
                                    }
                                    onAdd={props.onAdd}
                                    isBottom={index === length - 1}
                                    currentProject={props.currentProject}
                                    editMode={props.editMode}
                                    initialSelectedQuestionID={
                                        props.initialSelectedQuestionID
                                    }
                                    showError={props.showError}
                                />
                            );
                        })}
                    </div>
                )}
            </div>
        );
    }
}
const DragHandle = SortableHandle(() => <div className="q-card-drag"></div>);

const DefaultSortableGroupCard = SortableElement(SortableGroupCard);

export default DefaultSortableGroupCard;
